// How many tiers the level in play has (MUS-651).
//
// The count comes from the level's tier list. When that list does not load the
// count is not known, and the lesson says so (null) instead of guessing: a
// guess of 1 marked the level complete after any tier and sent Next to the next
// level, and the raw tier list stored in its place made every comparison with
// it false.

type TierProgress = {
  current_tier?: number | null;
  highest_tier_complete?: number | null;
};

const isWholeCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 1;

/**
 * The tier count a tier list, or a count already worked out, stands for: the
 * list's length, or the count itself. Null for anything else (an empty list,
 * no answer, a count that is not a whole number of at least 1).
 */
export function tierCountOf(value: unknown): number | null {
  if (Array.isArray(value)) return value.length >= 1 ? value.length : null;
  return isWholeCount(value) ? value : null;
}

/**
 * The level's tier count from the tier lists loaded at startup, or null when
 * they hold no entry for the level, or were not loaded by this run of the app:
 * a stored copy from an earlier run can predate tiers added since, and a
 * player on one of those would be capped below their tier and have it written.
 */
export function startupTierCountFor(
  app: { tiersByLevels?: unknown; tiersByLevelsLoadedThisRun?: boolean } | null | undefined,
  levelNumber: number,
): number | null {
  const tiersByLevels = app?.tiersByLevelsLoadedThisRun === true ? app.tiersByLevels : undefined;
  if (!Array.isArray(tiersByLevels)) return null;
  const entry: unknown = tiersByLevels.find(
    (candidate: unknown) =>
      typeof candidate === 'object' &&
      candidate !== null &&
      (candidate as { level_number?: unknown }).level_number === levelNumber,
  );
  return entry ? tierCountOf((entry as { tiers?: unknown }).tiers) : null;
}

/** Whether `tier` is the level's last tier (or past it). False while the count is not known. */
export function isLastTierOfLevel(
  tier: number | null | undefined,
  tierCount: number | null,
): boolean {
  return tierCount !== null && typeof tier === 'number' && tier >= tierCount;
}

/**
 * How many tiers the lesson can show: the level's count when it is known, and
 * otherwise the tiers the player has been on or completed, which exist.
 */
export function tierCountToShow(tierCount: number | null, progress?: TierProgress | null): number {
  if (tierCount !== null) return tierCount;
  return Math.max(1, progress?.current_tier ?? 1, progress?.highest_tier_complete ?? 0);
}

/**
 * The tier Next opens after a tier that did not finish the level: the next
 * one when the level's tier count is known, and the same one while it is not
 * (the next one may not exist: the session would be refused, and the lesson
 * would not load).
 */
export function tierAfterNext(tier: number | null | undefined, tierCount: number | null): number {
  const currentTier = typeof tier === 'number' && tier >= 1 ? tier : null;
  if (currentTier === null) return 1;
  return tierCount === null ? currentTier : currentTier + 1;
}
